import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Index } from 'react-virtualized';
import { RenderedRows } from 'react-virtualized/dist/es/List';
import { LIST_ROW_HEIGHTS } from '../../../../constants/list_row_heights';
import { IAthlete, IListRowRenderProps, ITeam } from '../../../../data/types';
import { List, ListSectionHeading } from '../../../shared/List/List';
import { ListAvatar } from '../../../shared/List/ListAvatar';
import { ListItem, ListItemCell } from '../../../shared/List/ListItem';
import {
  groupByParentEntity,
  IGroupedEntityListItem,
} from '../../../shared/List/listUtils';
import { NoResults } from '../../../shared/NoResults';
import { ManageAthletePopup } from './ManageAthletePopup';

interface IProps extends RouteComponentProps {
  loading: boolean;
  athletes: IAthlete[];
  teams: ITeam[];
  selectedAthleteId: string;
  onAthleteChange: (id: string) => void;
  filterApplied: boolean;
  clearFilter: () => void;
  isMobile: boolean;
}

export const AthleteList = withRouter((props: IProps): React.ReactElement => {
  const [heading, setHeading] = React.useState<string>();
  const [initialIndex, setInitialIndex] = React.useState<number>();
  /**
   * @description render function for list items
   * @param param0
   */
  const rowRenderer = ({
    index,
    key,
    style,
  }: IListRowRenderProps): React.ReactElement => {
    const row = data[index];
    if (row.isSticky) {
      return (
        <ListSectionHeading key={key} style={style} label={row.sectionLabel} />
      );
    } else {
      return (
        <AthleteListItem
          style={style}
          athlete={row.data}
          key={key}
          onClick={(): void => props.onAthleteChange(row.data.id)}
          selected={props.selectedAthleteId === row.data.id}
        />
      );
    }
  };

  /**
   * @description creates a memoized list of items grouped by team that updates when props update
   */
  const data = React.useMemo(
    (): IGroupedEntityListItem[] =>
      groupByParentEntity(props.teams, props.athletes, 'teams'),
    [props.teams, props.athletes, props.loading, props.selectedAthleteId]
  );

  /**
   * @description sets the current sticky header based on the rows that are currently showing
   * @param param0
   */
  const onRowsRendered = ({ startIndex }: RenderedRows): void => {
    if (data.length) {
      setHeading(data[startIndex].sectionLabel);
    }
  };

  /**
   * @description sets the initial index to scroll to on first render
   */
  React.useEffect((): void => {
    const selectedIndex = data.findIndex(
      (item: IGroupedEntityListItem): boolean => {
        return item.data.id === props.selectedAthleteId;
      }
    );
    setInitialIndex(selectedIndex);
  }, [props.isMobile ? props.selectedAthleteId : null]);

  const computeGroupedListRowHeight = (info: Index): number =>
    data[info.index]?.isSticky
      ? LIST_ROW_HEIGHTS.stickyHeader
      : LIST_ROW_HEIGHTS.normal;

  return data.length ? (
    <List
      rowHeight={computeGroupedListRowHeight}
      rowRenderer={rowRenderer}
      listLength={data.length}
      onRowsRendered={onRowsRendered}
      stickyHeading={heading}
      scrollTo={initialIndex}
    />
  ) : (
    <NoResults
      message='No Athletes Found'
      filterApplied={props.filterApplied}
      clearFilter={props.clearFilter}
    />
  );
});

interface IListItemProps {
  athlete: IAthlete;
  onClick: (id: string) => void;
  style: React.CSSProperties;
  selected: boolean;
}

const AthleteListItem = (props: IListItemProps): React.ReactElement => {
  const selectHandler = (): void => props.onClick(props.athlete.id);

  return (
    <ListItem
      onClick={selectHandler}
      style={props.style}
      selected={props.selected}
    >
      <ListItemCell style={{ minWidth: 0, overflow: 'hidden', flex: 2 }}>
        <ListAvatar entity={props.athlete} />
      </ListItemCell>
      <ListItemCell>
        <ManageAthletePopup athlete={props.athlete} />
      </ListItemCell>
    </ListItem>
  );
};
